import React, { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import { useLenis } from "@/hooks/useLenis";
import { UtilityCustomerBar, MainHeader } from "@/components/Header";
import { HeroSection } from "@/components/Hero";
import { EthosMarquee } from "@/components/CustomerActionPanel";
import { AppSection } from "@/components/AppSection";
import { AboutSection } from "@/components/AboutSection";
import { IvaSection } from "@/components/IvaSection";
import { SupportTimeline } from "@/components/SupportTimeline";
import { VulnerableSupport } from "@/components/VulnerableSupport";
import { ReviewsSection } from "@/components/ReviewsSection";
import { DocumentsLibrary } from "@/components/DocumentsLibrary";
import { FaqSection } from "@/components/FaqSection";
import { ContactSection } from "@/components/ContactSection";
import { ComplaintsSection, RegulatorySection } from "@/components/ComplaintsRegulatory";
import { SiteFooter } from "@/components/SiteFooter";
import { CookieConsent } from "@/components/CookieConsent";
import { MobileCustomerActionBar } from "@/components/MobileCustomerActionBar";
import { CookiePolicyPage } from "@/pages/CookiePolicy";
import { AccessibilityPage } from "@/pages/Accessibility";
import { LiveChatPage } from "@/pages/LiveChat";
import { TryOurAppPage } from "@/pages/TryOurApp";
import { IvaStandardConditionsPage } from "@/pages/IvaStandardConditions";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicy";
import { applySeo } from "@/lib/seo";

const CONSENT_KEY = "fj_cookie_consent";

const LandingPage = () => {
  useEffect(() => {
    applySeo();
    // Arriving with a section anchor (e.g. "/#faqs" from a legal page) — jump to it.
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) window.setTimeout(() => el.scrollIntoView(), 60);
    }
  }, []);

  return (
    <>
      <HeroSection />
      <EthosMarquee />
      <AppSection />
      <AboutSection />
      <IvaSection />
      <SupportTimeline />
      <VulnerableSupport />
      <ReviewsSection />
      <DocumentsLibrary />
      <FaqSection />
      <ContactSection />
      <ComplaintsSection />
      <RegulatorySection />
    </>
  );
};

const Site = () => {
  useLenis();
  const { pathname } = useLocation();

  const [cookieOpen, setCookieOpen] = useState(false);
  const [hasDecision, setHasDecision] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      setHasDecision(false);
      setCookieOpen(true);
    }
  }, []);

  // Back to the top on route change (section anchors are handled by LandingPage).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);

  const decide = (value) => {
    localStorage.setItem(CONSENT_KEY, value);
    setHasDecision(true);
    setCookieOpen(false);
  };

  return (
    <div className="relative min-h-screen bg-brand-bg font-body text-brand-body">
      <a href="#top" className="skip-link">Skip to content</a>

      <UtilityCustomerBar />
      <MainHeader />

      <main id="main">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/cookie-policy"
            element={<CookiePolicyPage onOpenCookies={() => setCookieOpen(true)} />}
          />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/live-chat" element={<LiveChatPage />} />
          <Route path="/try-our-app" element={<TryOurAppPage />} />
          <Route path="/iva-standard-conditions" element={<IvaStandardConditionsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <SiteFooter />

      <MobileCustomerActionBar />

      <CookieConsent
        open={cookieOpen}
        dismissible={hasDecision}
        onAccept={() => decide("accepted")}
        onReject={() => decide("rejected")}
        onClose={() => setCookieOpen(false)}
      />
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <Site />
    </BrowserRouter>
  );
}
